import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { AdminUser, Permission, AdminRole } from '../types'
import { authService } from '../services/auth-service'

interface AuthState {
  user: AdminUser | null
  isAuthenticated: boolean
  isLoading: boolean
  requiresMFA: boolean
  tempToken: string | null
  
  // Actions
  setUser: (user: AdminUser | null) => void
  setRequiresMFA: (requires: boolean) => void
  setLoading: (loading: boolean) => void
  login: (email: string, password: string) => Promise<void>
  verifyMFA: (code: string) => Promise<void>
  logout: () => Promise<void>
  loadUser: () => Promise<void>
  
  // Permission checks
  hasPermission: (permission: Permission) => boolean
  hasAnyPermission: (permissions: Permission[]) => boolean
  hasAllPermissions: (permissions: Permission[]) => boolean
  hasRole: (role: AdminRole) => boolean
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      requiresMFA: false,
      tempToken: null,

  setUser: (user) => set({ user, isAuthenticated: !!user }),
  
  setRequiresMFA: (requires) => set({ requiresMFA: requires }),
  
  setLoading: (loading) => set({ isLoading: loading }),

  login: async (email, password) => {
    set({ isLoading: true })
    try {
      const response = await authService.login({ email, password })
      
      if (response.mfa_required || response.mfa_setup_required || response.requiresMFA || response.status === 'mfa_required' || response.status === 'mfa_setup_required') {
        set({ 
            requiresMFA: true, 
            tempToken: response.temp_token ?? null,
            isLoading: false 
        })
      } else if (response.user && response.tokens) {
        authService.storeTokens(response.tokens.access_token, response.tokens.refresh_token)
        await get().loadUser()
        set({ 
          requiresMFA: false,
          tempToken: null,
          isLoading: false 
        })
      }
    } catch (error) {
      set({ isLoading: false })
      throw error
    }
  },

  verifyMFA: async (code) => {
    set({ isLoading: true })
    const { tempToken } = get()
    
    if (!tempToken) {
        set({ isLoading: false })
        throw new Error('Session expired')
    }

    try {
      const response = await authService.verifyMFA({ code, temp_token: tempToken })
      
      if (response.user && response.tokens) {
        authService.storeTokens(response.tokens.access_token, response.tokens.refresh_token)
        await get().loadUser()
        set({ 
            requiresMFA: false,
            tempToken: null,
            isLoading: false 
        })
      }
    } catch (error) {
      set({ isLoading: false })
      throw error
    }
  },

      logout: async () => {
        set({ isLoading: true })
        try {
          await authService.logout()
        } catch (error) {
          console.error('Logout error:', error)
        } finally {
          set({ 
            user: null, 
            isAuthenticated: false, 
            requiresMFA: false,
            isLoading: false 
          })
        }
      },

      loadUser: async () => {
        if (!authService.isAuthenticated()) {
          set({ user: null, isAuthenticated: false })
          return
        }

        set({ isLoading: true })
        try {
          const user = await authService.getCurrentAdmin()
          set({ user, isAuthenticated: true, isLoading: false })
        } catch (error) {
          authService.clearTokens()
          set({ user: null, isAuthenticated: false, isLoading: false })
        }
      },

      hasPermission: (permission) => {
        const { user } = get()
        if (!user) return false
        if (user.role === 'SUPER_ADMIN') return true
        if (!user.permissions) return false
        return user.permissions.includes(permission)
      },

      hasAnyPermission: (permissions) => {
        const { user } = get()
        if (!user) return false
        if (user.role === 'SUPER_ADMIN') return true
        if (!user.permissions || !Array.isArray(user.permissions)) return false
        if (!Array.isArray(permissions)) return false
        return permissions.some(p => user.permissions.includes(p))
      },

      hasAllPermissions: (permissions) => {
        const { user } = get()
        if (!user) return false
        if (user.role === 'SUPER_ADMIN') return true
        if (!user.permissions) return false
        return permissions.every(p => user.permissions.includes(p))
      },

      hasRole: (role) => {
        const { user } = get()
        if (!user) return false
        return user.role === role
      },
    }),
    {
      name: 'admin-auth-storage',
      partialize: (state) => ({ user: state.user, isAuthenticated: state.isAuthenticated }),
    }
  )
)
